import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import * as Papa from 'papaparse';
import { AuthService } from '../auth/auth.service';
import { IHistoryDeleteBatchResponse } from '../models/IHistoryDeleteBatch';
import { IQuerySubmission, QueryResultRow } from '../models/IQuerySubmission';
import { environment } from '../../environments/environment';
import { IHistoryPageResponse } from '../models/IHistoryResult';

@Injectable({ providedIn: 'root' })
export class SqlQueryService {
  private readonly apiBase = `${environment.API_BASE_URL}/api`;
  private readonly historyBase = `${environment.API_BASE_URL}/api/history`;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {}

  submitQuery(query: string): Observable<IQuerySubmission> {
    return this.http
      .post<unknown>(
        `${this.apiBase}/query`,
        { query },
        { headers: this.authHeaders(true) }, // prefer ID token for user identity/email
      )
      .pipe(map((response) => this.normalizeJobLookupResponse(response, '', { query })));
  }

  fetchSubmissionMeta(querySubmission: IQuerySubmission): Observable<IQuerySubmission> {
    const jobId = (querySubmission.job_id || '').trim();
    if (!jobId) return of(querySubmission);
    return this.fetchQueryByJobId(jobId, querySubmission);
  }

  fetchQueryByJobId(
    jobId: string,
    fallbackSubmission?: Partial<IQuerySubmission> | null,
  ): Observable<IQuerySubmission> {
    const encodedJobId = encodeURIComponent(jobId);
    return this.http
      .get<unknown>(`${this.apiBase}/query/${encodedJobId}`, {
        headers: this.authHeaders(true),
      })
      .pipe(
        map((response) => this.normalizeJobLookupResponse(response, jobId, fallbackSubmission)),
      );
  }

  fetchCsvResults(
    resultUrl: string,
    expectedRowCount?: number | null,
  ): Observable<QueryResultRow[]> {
    return this.fetchCsvText(resultUrl).pipe(
      map((csvText) => this.parseCsvRows(csvText, expectedRowCount)),
    );
  }

  fetchCsvText(resultUrl: string): Observable<string> {
    return this.http.get(resultUrl, { responseType: 'text' });
  }

  getUserHistoryPage(
    uid: string,
    limit = 25,
    cursor: string | null = null,
  ): Observable<IHistoryPageResponse> {
    let params = new HttpParams().set('limit', String(limit));
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<IHistoryPageResponse>(`${this.historyBase}/${this.safeUser(uid)}`, {
      headers: this.authHeaders(true),
      params,
    });
  }

  buildHistoryObjectUrl(key: string): string {
    const encodedKey = this.encodeS3KeyPreservingSlashes(key);
    return `https://${environment.AWS_S3_BUCKET}.s3.amazonaws.com/${encodedKey}`;
  }

  deleteHistoryBatch(uid: string, filenames: string[]): Observable<IHistoryDeleteBatchResponse> {
    return this.http.post<IHistoryDeleteBatchResponse>(
      `${this.historyBase}/${this.safeUser(uid)}/delete`,
      { filenames },
      { headers: this.authHeaders(true) },
    );
  }

  private authHeaders(preferIdToken = false): HttpHeaders {
    const tokens = this.auth.tokens();
    const token = preferIdToken ? tokens?.idToken : tokens?.accessToken;
    if (!token) return new HttpHeaders();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  private normalizeJobLookupResponse(
    payload: unknown,
    fallbackJobId: string,
    fallbackSubmission?: Partial<IQuerySubmission> | null,
  ): IQuerySubmission {
    const root = this.asRecord(payload);
    const topLevelData = this.asRecord(root?.['data']);
    const submissionRecord =
      this.findSubmissionRecord(root) ??
      this.findSubmissionRecord(topLevelData) ??
      this.findSubmissionRecord(this.asRecord(topLevelData?.['submission']));
    const resolved = submissionRecord ?? {};
    const authEmail = this.auth.user()?.email || '';

    return this.mergeSubmissionHints(
      {
        job_id: this.toStringValue(resolved['job_id']) || fallbackJobId,
        query: this.toStringValue(resolved['query']) || '',
        status: this.coerceStatus(this.toStringValue(resolved['status'])?.toLowerCase()),
        username:
          this.toStringValue(resolved['username']) ||
          this.toStringValue(resolved['uid']) ||
          authEmail,
        uid: this.toNullableString(resolved['uid']) ?? undefined,
        creation_time: this.toStringValue(resolved['creation_time']) || '',
        query_s3_key: this.toNullableString(resolved['query_s3_key']),
        result_s3_key: this.toNullableString(resolved['result_s3_key']),
        result_url: this.toNullableString(resolved['result_url']),
        result_content_type: this.toNullableString(resolved['result_content_type']),
        result_content_encoding: this.toNullableString(resolved['result_content_encoding']),
        result_row_count: this.toNullableNumber(resolved['result_row_count']),
        error_message: this.toNullableString(resolved['error_message']),
        result_message: this.toNullableString(resolved['result_message']),
      },
      fallbackSubmission,
    );
  }

  private findSubmissionRecord(
    value: Record<string, unknown> | null,
  ): Record<string, unknown> | null {
    if (!value) return null;
    if (this.looksLikeSubmission(value)) return value;
    const nestedCandidates = [
      this.asRecord(value['submission']),
      this.asRecord(value['query']),
      this.asRecord(value['job']),
      this.asRecord(value['meta']),
    ];
    for (const candidate of nestedCandidates) {
      if (candidate && this.looksLikeSubmission(candidate)) return candidate;
    }
    return null;
  }

  private looksLikeSubmission(value: Record<string, unknown>): boolean {
    return (
      typeof value['job_id'] === 'string' ||
      typeof value['status'] === 'string' ||
      typeof value['query'] === 'string' ||
      typeof value['creation_time'] === 'string' ||
      typeof value['result_url'] === 'string' ||
      typeof value['result_s3_key'] === 'string' ||
      typeof value['query_s3_key'] === 'string'
    );
  }

  private parseCsvRows(csvText: string, expectedRowCount?: number | null): QueryResultRow[] {
    const parsed = Papa.parse<Record<string, unknown>>(csvText, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
    });

    const fatalErrors = parsed.errors.filter((error) => error.code !== 'UndetectableDelimiter');
    if (fatalErrors.length > 0) {
      const firstError = fatalErrors[0];
      throw new Error(firstError?.message || 'Failed to parse CSV result file.');
    }

    const positiveExpectedRowCount =
      typeof expectedRowCount === 'number' && expectedRowCount > 0 ? expectedRowCount : null;
    if (positiveExpectedRowCount && parsed.data.length !== positiveExpectedRowCount) {
      const headerlessRows = this.parseHeaderlessCsvRows(csvText);
      if (headerlessRows.length === positiveExpectedRowCount) return headerlessRows;
    }

    return parsed.data;
  }

  private parseHeaderlessCsvRows(csvText: string): QueryResultRow[] {
    const parsed = Papa.parse<unknown[]>(csvText, {
      header: false,
      skipEmptyLines: true,
    });

    const fatalErrors = parsed.errors.filter((error) => error.code !== 'UndetectableDelimiter');
    if (fatalErrors.length > 0) {
      const firstError = fatalErrors[0];
      throw new Error(firstError?.message || 'Failed to parse CSV result file.');
    }

    return parsed.data.map((row) => {
      const values = Array.isArray(row) ? row : [row];
      return values.reduce<QueryResultRow>((acc, value, index) => {
        acc[`column_${index + 1}`] = value;
        return acc;
      }, {});
    });
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private toStringValue(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
  }

  private toNullableNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }

  private coerceStatus(status: string | undefined): IQuerySubmission['status'] {
    switch (status) {
      case 'pending':
      case 'queued':
      case 'running':
      case 'processing':
      case 'success':
      case 'partial':
      case 'error':
        return status;
      default:
        return 'error';
    }
  }

  private mergeSubmissionHints(
    submission: IQuerySubmission,
    hint?: Partial<IQuerySubmission> | null,
  ): IQuerySubmission {
    if (!hint) return submission;
    return {
      ...submission,
      job_id: submission.job_id || this.stringOrEmpty(hint.job_id),
      query: submission.query || this.stringOrEmpty(hint.query),
      username: submission.username || this.stringOrEmpty(hint.username),
      uid: submission.uid || this.nullableStringFromHint(hint.uid) || undefined,
      creation_time: submission.creation_time || this.stringOrEmpty(hint.creation_time),
      query_s3_key: submission.query_s3_key || this.nullableStringFromHint(hint.query_s3_key),
      result_s3_key: submission.result_s3_key || this.nullableStringFromHint(hint.result_s3_key),
      result_url: submission.result_url || this.nullableStringFromHint(hint.result_url),
      result_content_type:
        submission.result_content_type || this.nullableStringFromHint(hint.result_content_type),
      result_content_encoding:
        submission.result_content_encoding ||
        this.nullableStringFromHint(hint.result_content_encoding),
      result_row_count:
        submission.result_row_count ?? this.nullableNumberFromHint(hint.result_row_count),
      error_message: submission.error_message || this.nullableStringFromHint(hint.error_message),
      result_message: submission.result_message || this.nullableStringFromHint(hint.result_message),
    };
  }

  private stringOrEmpty(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private nullableStringFromHint(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
  }

  private nullableNumberFromHint(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private safeUser(input: string): string {
    return (input || 'user').toLowerCase().replace(/[@.]/g, '-');
  }

  private encodeS3KeyPreservingSlashes(key: string): string {
    return (key || '')
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
  }
}
